import { useEffect, useRef, useState, type ReactNode } from 'react';
import './MobPullToRefresh.css';

interface MobPullToRefreshProps {
  /** Called when the user pulls past PULL_THRESHOLD and releases. */
  onRefresh: () => void;
  /** External refresh state (from TanStack Query.isFetching, etc.). */
  isRefreshing: boolean;
  children: ReactNode;
}

const PULL_THRESHOLD = 70; // px past which release triggers refresh
const MAX_PULL = 120;      // px cap on the visual pull distance

/**
 * Native touch-based pull-to-refresh. Wraps the scrollable content — the
 * component itself is the scroll container. Only triggers when the user
 * starts a downward pull while already at scrollTop === 0.
 *
 * Non-touch input (desktop mouse) is untouched; use the header refresh
 * button instead. Respects prefers-reduced-motion via the .is-active class.
 */
export function MobPullToRefresh({
  onRefresh,
  isRefreshing,
  children,
}: MobPullToRefreshProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const startYRef = useRef<number | null>(null);
  const pullRef = useRef(0);
  const onRefreshRef = useRef(onRefresh);
  const [pull, setPull] = useState(0);

  // Keep the latest onRefresh accessible from touch handlers without
  // re-attaching them (which would cost perf on every parent re-render).
  useEffect(() => {
    onRefreshRef.current = onRefresh;
  }, [onRefresh]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const updatePull = (v: number) => {
      pullRef.current = v;
      setPull(v);
    };

    const onStart = (e: TouchEvent) => {
      if (el.scrollTop > 0) return;
      startYRef.current = e.touches[0].clientY;
    };

    const onMove = (e: TouchEvent) => {
      if (startYRef.current === null) return;
      if (el.scrollTop > 0) {
        startYRef.current = null;
        updatePull(0);
        return;
      }
      const dy = e.touches[0].clientY - startYRef.current;
      if (dy <= 0) {
        updatePull(0);
        return;
      }
      // Rubber-band damping — the further you pull, the harder it gets.
      updatePull(Math.min(dy * 0.5, MAX_PULL));
    };

    const onEnd = () => {
      if (startYRef.current === null) return;
      if (pullRef.current >= PULL_THRESHOLD) {
        onRefreshRef.current();
      }
      startYRef.current = null;
      updatePull(0);
    };

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: true });
    el.addEventListener('touchend', onEnd);
    el.addEventListener('touchcancel', onEnd);

    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, []);

  const showIndicator = pull > 0 || isRefreshing;
  const progress = Math.min(pull / PULL_THRESHOLD, 1);
  const contentOffset = isRefreshing ? 40 : pull;

  return (
    <div className="mob-ptr" ref={scrollRef}>
      {showIndicator ? (
        <div
          className={`mob-ptr__indicator${isRefreshing ? ' is-active' : ''}`}
          aria-hidden="true"
        >
          <div
            className={`mob-ptr__spinner${isRefreshing ? ' is-spinning' : ''}`}
            style={
              !isRefreshing
                ? { transform: `rotate(${progress * 360}deg)`, opacity: 0.3 + progress * 0.7 }
                : undefined
            }
          />
        </div>
      ) : null}
      <div
        className="mob-ptr__content"
        style={
          pull > 0 || isRefreshing
            ? {
                transform: `translateY(${contentOffset}px)`,
                transition: pull > 0 ? 'none' : 'transform 200ms ease-out',
              }
            : undefined
        }
      >
        {children}
      </div>
    </div>
  );
}
